/**
 * Settings — application-wide
 *
 * Settings that belong to the application rather than to a brand or the connector. Sync
 * Settings is next door and owns Mirakl's call-frequency budget and the overnight schedule;
 * nothing here is about either.
 *
 * TODAY THERE IS ONE SETTING, and the page exists anyway. The addresses below are the list
 * the whole application emails from, so the next notice is a caller rather than a second
 * recipient setting, a second template, and a second place for an address to go stale.
 *
 * EMPTY IS SHOWN, NOT IMPLIED. An admin may legitimately choose that nobody is emailed, but
 * an empty box reads as configured, and the first notice nobody receives is the one about a
 * shipping location that is going unbilled. So the state says itself.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Page, Card, Text, Box, BlockStack, InlineStack, Button, TextField, Banner, Spinner,
} from '@shopify/polaris';
import { useAppSettings, useUpdateAppSettings } from '../utils/queries';

/**
 * The same shape the server enforces.
 *
 * Checked here as well so a typo is answered as the admin leaves the field, rather than
 * after a save round-trip. The server is still the authority — this is the courtesy copy.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmail = (value) => EMAIL.test(String(value || '').trim());

const when = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function Settings() {
  const { data, isLoading, isError, error } = useAppSettings();
  const update = useUpdateAppSettings();

  // One empty row when nothing is stored, so the field is there to type into rather than
  // behind an "add" the admin has to find first.
  const [emails, setEmails] = useState(['']);
  const [saved, setSaved] = useState(false);

  const stored = useMemo(
    () => data?.settings?.supportNotificationEmails || [],
    [data],
  );

  useEffect(() => {
    setEmails(stored.length > 0 ? [...stored] : ['']);
  }, [stored]);

  const filled = emails.map((e) => e.trim()).filter((e) => e !== '');
  const invalid = filled.filter((e) => !isEmail(e));
  // Compared against what is stored so Save is only live when something actually differs —
  // including a removal, which a length check alone would miss.
  const dirty = JSON.stringify(filled.map((e) => e.toLowerCase())) !== JSON.stringify(stored);

  const setAt = (i, value) => {
    const next = [...emails];
    next[i] = value;
    setEmails(next);
    setSaved(false);
  };

  const removeAt = (i) => {
    const next = emails.filter((_, n) => n !== i);
    setEmails(next.length > 0 ? next : ['']);
    setSaved(false);
  };

  const save = async () => {
    await update.mutateAsync({ supportNotificationEmails: filled });
    setSaved(true);
  };

  if (isLoading) {
    return (
      <Page title="Settings" fullWidth>
        <Card><Box padding="400"><InlineStack gap="200" blockAlign="center">
          <Spinner size="small" /><Text as="span">Loading settings…</Text>
        </InlineStack></Box></Card>
      </Page>
    );
  }

  if (isError) {
    return (
      <Page title="Settings" fullWidth>
        <Banner tone="critical" title="Could not load settings">
          <p>{error?.message || 'Please try again.'}</p>
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Settings" subtitle="Settings that apply to the whole application" fullWidth>
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">Support notification emails</Text>
              <Text as="p" tone="subdued">
                Who is emailed when the application needs someone to look at something.
                Everyone listed receives every notice.
              </Text>
            </BlockStack>

            {/* The state that would otherwise be invisible. An empty list is a valid choice
                and silently stops every notice, so it is said rather than left to be noticed
                when a notice does not arrive. */}
            {stored.length === 0 && (
              <Banner tone="warning" title="Nobody is being emailed">
                <p>
                  No addresses are configured, so notices appear in the console only. Add an
                  address to have them emailed as well.
                </p>
              </Banner>
            )}

            {update.isError && (
              <Banner tone="critical" title="Could not save">
                <p>{update.error?.message || 'Please try again.'}</p>
              </Banner>
            )}

            {saved && !update.isError && !dirty && (
              <Banner tone="success" title="Saved" onDismiss={() => setSaved(false)} />
            )}

            <BlockStack gap="200">
              {emails.map((value, i) => (
                // Index as key, deliberately: these rows have no identity of their own, and
                // the value cannot serve as one while it is half-typed or duplicated.
                // eslint-disable-next-line react/no-array-index-key
                <InlineStack key={i} gap="200" blockAlign="start" wrap={false}>
                  <Box width="100%">
                    <TextField
                      label={i === 0 ? 'Email address' : ''}
                      labelHidden={i !== 0}
                      type="email"
                      autoComplete="email"
                      value={value}
                      onChange={(v) => setAt(i, v)}
                      placeholder="name@2wbrands.com"
                      error={value.trim() !== '' && !isEmail(value) ? 'Not an email address' : undefined}
                    />
                  </Box>
                  <Box paddingBlockStart={i === 0 ? '600' : '0'}>
                    <Button
                      variant="tertiary"
                      tone="critical"
                      onClick={() => removeAt(i)}
                      disabled={emails.length === 1 && value.trim() === ''}
                      accessibilityLabel={`Remove ${value || 'this address'}`}
                    >
                      Remove
                    </Button>
                  </Box>
                </InlineStack>
              ))}
            </BlockStack>

            <InlineStack gap="200">
              <Button onClick={() => { setEmails([...emails, '']); setSaved(false); }}>
                Add address
              </Button>
              <Button
                variant="primary"
                onClick={save}
                loading={update.isPending}
                disabled={!dirty || invalid.length > 0}
              >
                Save
              </Button>
            </InlineStack>

            <Text as="p" tone="subdued" variant="bodySm">
              Last changed {when(data?.settings?.updatedAt)}
              {data?.settings?.updatedBy ? ` by ${data.settings.updatedBy}` : ''}
            </Text>
          </BlockStack>
        </Box>
      </Card>
    </Page>
  );
}
